import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Bot } from 'lucide-react';
import VedicAiChatModal from './VedicAiChatModal';
import '../styles/bot-welcome-popup.css';

const BotWelcomePopup = () => {
  const [isBubbleVisible, setIsBubbleVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState(null);
  const [typedText, setTypedText] = useState('');
  const fullMessage = "Namaste 🙏! How can I help you explore ancient Shalya Tantra surgical wisdom today?";

  useEffect(() => {
    // Show speech bubble after 800ms delay on page load
    const timer = setTimeout(() => {
      setIsBubbleVisible(true);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  // Live typing effect when speech bubble becomes visible
  useEffect(() => {
    if (isBubbleVisible && !isModalOpen) {
      setTypedText('');
      let i = 0;
      const typeInterval = setInterval(() => {
        if (i < fullMessage.length) {
          setTypedText(fullMessage.slice(0, i + 1));
          i++;
        } else {
          clearInterval(typeInterval);
        }
      }, 25);
      return () => clearInterval(typeInterval);
    }
  }, [isBubbleVisible, isModalOpen]);

  const handleOpenChat = (query = null) => {
    setIsBubbleVisible(false);
    setInitialQuery(query);
    setIsModalOpen(true);
  };

  const handleDismissBubble = (e) => {
    e.stopPropagation();
    setIsBubbleVisible(false);
  };

  return (
    <div className="bot-welcome-wrapper">
      {/* Floating Holographic Launcher Orb Button */}
      <motion.button
        className="bot-holographic-orb-launcher"
        onClick={() => handleOpenChat()}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Open Vedic AI Assistant"
        title="Ask Vedic AI Assistant ✨"
      >
        <div className="orb-pulse-ring-outer" />
        <div className="orb-pulse-ring-inner" />
        <div className="orb-core-icon">
          <Bot size={24} className="bot-orb-svg" />
        </div>
        <span className="orb-status-dot" />
      </motion.button>

      {/* Speech Bubble Teaser Drop Pill (Attached to Launcher Orb) */}
      <AnimatePresence>
        {isBubbleVisible && !isModalOpen && (
          <motion.div
            className="bot-orb-speech-bubble"
            onClick={() => handleOpenChat()}
            initial={{ opacity: 0, scale: 0.85, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            transition={{ type: 'spring', damping: 20, stiffness: 200 }}
          >
            <div className="speech-bubble-header">
              <div className="speech-bubble-title">
                <img src="/bot-avatar.svg" alt="Vedic AI Logo" className="bubble-avatar-img" />
                <span>Vedic AI Assistant</span>
                <Sparkles size={13} className="sparkle-blue" />
              </div>
              <button
                className="speech-bubble-close"
                onClick={handleDismissBubble}
                title="Dismiss message"
              >
                <X size={14} />
              </button>
            </div>

            <p className="speech-bubble-text">
              {typedText}
              {typedText.length < fullMessage.length && <span className="typing-cursor">|</span>}
            </p>

            <div className="speech-bubble-arrow" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Expanded Custom Native Vedic AI Chat Drawer */}
      <VedicAiChatModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onMinimize={() => setIsModalOpen(false)}
        initialQuery={initialQuery}
      />
    </div>
  );
};

export default BotWelcomePopup;
